import React, { useMemo } from "react";
import { useRouteStore } from "../../store/useRouteStore";
import { useRoutePlayer } from "../../hooks/useRoutePlayer";
import { RouteProcessor } from "../../utils/routeUtils";
import { formatTime } from "../../utils/formatters";
import { cn } from "../../utils/helpers";

export const RouteTimeline: React.FC = () => {
  const { selectedRoute, playerState, seekTo } = useRouteStore();
  const { playbackFrames } = useRoutePlayer();

  const timelineSections = useMemo(() => {
    if (!selectedRoute) return [];

    const sections: Array<{
      key: string;
      type: "trip" | "gap";
      widthPercent: number;
      label: string;
      frameStart: number;
      avgSpeed?: number;
    }> = [];

    if (playbackFrames.length === 0) return sections;

    let frameStart = 0;

    selectedRoute.trips.forEach((trip, tripIndex) => {
      const tripFrameCount = trip.coordinates.length;
      const gap = selectedRoute.trip_gaps.find(
        (g) => g.from_trip_index === tripIndex,
      );
      const gapFrameCount = gap
        ? RouteProcessor.getGapAnimationSteps(gap.distance_km)
        : 0;
      const sectionFrames = tripFrameCount + gapFrameCount;
      const widthPercent = (sectionFrames / playbackFrames.length) * 100;

      const speeds = trip.coordinates.map((p) => p.speed || 0);
      const avgSpeed =
        speeds.length > 0
          ? speeds.reduce((a, b) => a + b, 0) / speeds.length
          : 0;

      sections.push({
        key: `trip-${tripIndex}`,
        type: "trip",
        widthPercent,
        label: `Segment ${tripIndex + 1}`,
        frameStart,
        avgSpeed,
      });

      frameStart += sectionFrames;
    });

    return sections;
  }, [selectedRoute, playbackFrames]);

  const getSpeedColor = (speed: number) => {
    if (speed < 5) return "bg-red-400";
    if (speed < 15) return "bg-yellow-400";
    if (speed < 25) return "bg-green-400";
    return "bg-blue-400";
  };

  const handleSectionClick = (frameStart: number) => {
    if (playbackFrames.length <= 1) return;
    const progress = (frameStart / (playbackFrames.length - 1)) * 100;
    seekTo(progress);
  };

  if (!selectedRoute) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <h4 className="font-medium text-gray-900 mb-4">Route Timeline</h4>

      <div className="space-y-3">
        <div className="flex items-center space-x-4 text-sm flex-wrap gap-y-1">
          <span className="text-gray-600">Speed:</span>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-red-400 rounded" />
            <span>0-5 km/h</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-yellow-400 rounded" />
            <span>5-15 km/h</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-400 rounded" />
            <span>15-25 km/h</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-slate-300 rounded border border-dashed border-slate-500" />
            <span>Gap / transport</span>
          </div>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Current position indicator */}
          <div
            className="absolute top-0 w-0.5 h-8 bg-red-500 z-10 pointer-events-none"
            style={{ left: `${playerState.progress}%` }}
          />

          {/* Timeline segments */}
          <div className="flex h-8 rounded overflow-hidden border border-gray-200">
            {timelineSections.map((section) => (
              <div
                key={section.key}
                className={cn(
                  "cursor-pointer hover:opacity-80 transition-opacity flex items-center justify-center text-[10px] text-white/90 font-medium overflow-hidden",
                  section.type === "trip"
                    ? getSpeedColor(section.avgSpeed ?? 0)
                    : "bg-slate-300 border-l border-r border-dashed border-slate-500 text-slate-700",
                )}
                style={{ width: `${section.widthPercent}%` }}
                onClick={() => handleSectionClick(section.frameStart)}
                title={
                  section.type === "trip"
                    ? `${section.label} · avg ${section.avgSpeed?.toFixed(1)} km/h`
                    : section.label
                }
              >
                {section.widthPercent > 8 ? section.label : ""}
              </div>
            ))}
          </div>

          <div className="flex justify-between mt-2 text-xs text-gray-500">
            <span>{formatTime(selectedRoute.start_time)}</span>
            <span>{formatTime(selectedRoute.end_time)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
